import BackgroundImage from 'public/images/shipskis/how-it-works-hero.jpg'

import { FAQProps } from 'lib/components/FAQ/FAQ'
import { FeatureListProps } from 'lib/components/FeatureList/FeatureList'
import { FiftyFiftyProps } from 'lib/components/FiftyFifty/FiftyFifty'
import { NarrativeHeroProps } from 'lib/components/NarrativeHero/NarrativeHero'
import { IconNames } from 'lib/settings/icons'

import InfoButton from '@/components/InfoButton'
import { redirectFromBookingWidget } from '@/utils/bookingWidgetRedirect'

import {
  FiftyFiftyTextFields,
  HowItWorksPageContent,
  InformationCardProps,
  VideoProps,
} from './shipsticks.page.content'

const BACKGROUND_IMAGE = typeof BackgroundImage === 'string' ? BackgroundImage : BackgroundImage.src

const hero: NarrativeHeroProps = {
  backgroundImage: BACKGROUND_IMAGE,
  header: (
    <>
      Winter travel, <b>made easy.</b>
    </>
  ),
  subHeader:
    'ShipSkis lets you skip baggage claim by shipping your winter gear (and luggage) ' +
    'to and from your destination.',
}

const steps: { header?: string; steps: string[] } = {
  header: 'Learn how to ship skis and snowboards in three easy steps',
  steps: ['Book', 'Ship', 'Play'],
}

const fiftyTexts: FiftyFiftyTextFields[] = [
  {
    header: (
      <>
        1. Schedule your <strong>shipment.</strong>
      </>
    ),
    image: '/images/shipskis/how-it-works-1.jpg',
    body: (
      <>
        <p className="leading-relaxed">
          Easily book online, or over the phone. We’re partnered with the top ski resorts and can
          ship your gear to just about anywhere in the world.
          <InfoButton
            tooltipText={
              'Choose one way, round-trip, or even multiple stops. Pro tip: Check that your ' +
              'destination can accept shipments.'
            }
            className="ml-1"
          />
        </p>
      </>
    ),
    eyebrow: {
      icon: {
        icon: 'number-1-circle' as IconNames,
      },
      text: 'Book',
    },
    button: {
      text: 'Get started',
      action: 'booking-widget-redirect',
    },
    showEyebrow: false,
  },
  {
    header: (
      <>
        2. Pack up your <strong>gear.</strong>
      </>
    ),
    image: '/images/shipskis/how-it-works-2.jpg',
    body: (
      <>
        <p className="leading-relaxed">
          Pack as you would for a flight and attach your provided label. We’ll pick your gear up at
          your home, office—even your resort or lodge. You can also drop off with the designated
          local carrier if you prefer.
          <InfoButton
            tooltipText={`Print your label from your confirmation email or from your account, or 
              email it to the local store of the carrier (FedEx, UPS, or DHL) to print`}
            className="ml-1"
          />
        </p>
      </>
    ),
    eyebrow: {
      icon: {
        icon: 'number-2-circle' as IconNames,
      },
      text: 'Ship',
    },
    button: {
      text: 'How to pack',
      link: {
        href: '/pack',
      },
    },
    showEyebrow: false,
  },
  {
    header: (
      <>
        3. Meet your skis at your <strong>destination.</strong>
      </>
    ),
    image: '/images/shipskis/how-it-works-3.jpg',
    body: (
      <>
        <p className="leading-relaxed">
          Enjoy flying (for a change) as you breeze by the baggage lines. Complimentary insurance,
          tracking, and an on-time guarantee make shipping your skis simple and stress-free.
          <InfoButton
            tooltipText={
              'Ask where you can expect to pick up your shipment once you \
                arrive—so when you do, you can start skiing as soon as possible.'
            }
            className="ml-1"
          />
        </p>
      </>
    ),
    eyebrow: {
      icon: {
        icon: 'number-3-circle' as IconNames,
      },
      text: 'Play',
    },
    button: {
      text: 'On-time guarantee',
      link: {
        href: '/guarantee',
      },
    },
    showEyebrow: false,
  },
]

const fifties: FiftyFiftyProps[] = fiftyTexts.map((text, index) => ({
  ...text,
  image: {
    src: text.image,
  },
  layout: index % 2 === 0 ? 'image-right' : 'image-left',
  backgroundColor: 'white',
  className: 'md:gap-6 lg:gap-12',
}))

const breaker: string = `
  We’re here to make winter travel easier, more reliable, and way less stressful.
  That’s why every shipment is backed by the ShipSkis guarantee.
`

const feature: FeatureListProps = {
  header: 'Our guarantee',
  className: 'py-12',
  items: [
    {
      icon: 'clock',
      header: 'On-time guarantee',
      body: (
        <>
          It’s easy to ship your skis and snowboards with ShipSkis. Just pick your trip dates and
          schedule your shipment.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '/guarantee',
        },
      },
    },
    {
      icon: 'shield-checkmark',
      header: 'Complimentary coverage',
      body: (
        <>
          It’s easy to ship your winter gear with ShipSkis. Just pick your trip dates and schedule
          your shipment.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
    {
      icon: 'customer-service',
      header: 'Customer support',
      body: (
        <>
          It’s easy to ship your winter gear with ShipSkis. Just pick your trip dates and schedule
          your shipment.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
    {
      icon: 'delivery-truck',
      header: 'Door-to-door service',
      body: (
        <>
          It’s easy to ship your winter gear with ShipSkis. Just pick your trip dates and schedule
          your shipment.
        </>
      ),
      button: {
        text: 'View more',
        link: {
          href: '#',
        },
      },
    },
  ],
}

const videos: VideoProps = {
  header: (
    <>
      Watch our videos <strong>to learn more</strong>
    </>
  ),
  cards: [],
}

const informationCards: InformationCardProps[] = [
  {
    title: 'What can I ship?',
    description: 'Ship your luggage, boxes, skis, or snowboard to your destination.',
    link: '/what-can-i-ship',
  },
  {
    title: 'Packing guides',
    description:
      'Read step-by-step instructions on how to package your items and securely attach ' +
      'the shipping label.',
    link: '/pack',
  },
  {
    title: 'Our guarantee',
    description: 'Affordable pricing, on-time delivery, and the best service are guaranteed.',
    link: '/guarantee',
  },
]

const faq: FAQProps = {
  subtitle: 'Please choose a topic below to better pinpoint your question you may have.',
  title: (
    <>
      Have questions?
      <br />
      <b>We have answers.</b>
    </>
  ),
  categories: [
    {
      categoryName: 'How It Works & Getting Started',
      categoryId: 1,
    },
    {
      categoryName: 'Packing & Preparing Your Shipment',
      categoryId: 2,
    },
    {
      categoryName: 'Pickup & Delivery',
      categoryId: 3,
    },
    {
      categoryName: 'Shipping & Tracking',
      categoryId: 4,
    },
    {
      categoryName: 'Pricing, Insurance & Claims',
      categoryId: 5,
    },
    {
      categoryName: 'Custom Orders',
      categoryId: 6,
    },
    {
      categoryName: 'Support',
      categoryId: 7,
    },
  ],
  items: [
    {
      question: 'What is ShipSkis?',
      answer: (
        <>
          <p>
            ShipSkis is a door-to-door shipping service that helps you travel more easily by sending
            your skis, snowboard, boots, bikes, luggage, and other gear ahead of your trip. We pick
            up from your home, office, resort, or hotel and deliver directly to your destination.
          </p>
          <p className="mt-4">
            By shipping ahead with ShipSkis, you can skip the stress of hauling heavy bags through
            airports, waiting at baggage claim, and dealing with lost or delayed luggage.
          </p>
        </>
      ),
      categoryId: 1,
    },
    {
      question: 'How do I create an account?',
      answer: (
        <>
          Navigate to ‘Log In’ in the top right corner of your screen. You can either create a new
          account or sign in to an existing one, but you will need one to book. Creating an account
          requires no commitment and only takes a few moments to complete.
        </>
      ),
      categoryId: 1,
    },
    {
      question: 'Is it free to create an account?',
      answer: (
        <>
          Yes, signing up is completely free! There&apos;s no cost or commitment to create an
          account.
        </>
      ),
      categoryId: 1,
    },
    {
      question: 'Who can use ShipSkis?',
      answer: (
        <>
          Anyone can use ShipSkis! Whether you&apos;re a seasoned skier, a first-time winter
          traveler, or shipping on behalf of someone else, our service is designed to make travel
          easier for everyone.
        </>
      ),
      categoryId: 1,
    },
    {
      question: 'How do I book a shipment?',
      answer: (
        <>
          Click{' '}
          <a
            className="font-bold text-blue-600 underline hover:text-blue-800"
            href="/book/ship"
            onClick={(e) => {
              e.preventDefault()
              redirectFromBookingWidget('')
            }}
          >
            here
          </a>{' '}
          to get started. Simply enter your pickup and delivery locations, select your travel dates,
          and choose the service that best fits your needs.
        </>
      ),
      categoryId: 1,
    },
    {
      question: 'What happens after I book?',
      answer: (
        <>
          Once you complete your booking, you&apos;ll receive your shipping labels and step-by-step
          instructions.
        </>
      ),
      categoryId: 1,
    },
    {
      question: 'How many days in advance should I schedule my gear to arrive?',
      answer: (
        <>
          We suggest scheduling your gear to arrive at least one business day prior to your arrival.
          For example, if you’re planning to ski on Monday, plan to have your gear delivered by the
          Friday before.
        </>
      ),
      categoryId: 1,
    },
    {
      question: 'What’s the difference between one-way and round-trip shipping?',
      answer: (
        <>
          One-way shipping means your winter gear is shipped from your starting point to your
          destination. Round-trip shipping includes shipping your gear to your destination and then
          back to your home or original location. There is no cost difference between two one-way
          shipments
        </>
      ),
      categoryId: 1,
    },
    {
      question: 'What other items can I ship using ShipSkis?',
      answer: (
        <>
          <p>
            Travel completely bag-free by shipping your luggage along with your skis, snowboard,
            boots, or bike. We can accommodate checked and oversized luggage.
          </p>
          <p className="mt-4">
            If you’re interested in shipping specialty items or arranging group or event shipments,
            please <a href="https://www.shipskis.com/contact">contact our team</a> so we can explore
            how to best accommodate your needs.
          </p>
        </>
      ),
      categoryId: 1,
    },
    {
      question: 'Where can I ship my gear?',
      answer: (
        <>
          You can send your shipment to any physical destination of your choosing, with the
          exception of P.O. boxes, airports, and countries we don’t currently service.
        </>
      ),
      categoryId: 1,
    },
    {
      question: 'What types of bags/containers are allowed?',
      answer: (
        <>
          Pack your skis or snowboard just like you would for the airlines, in a soft travel bag or
          hard case. You may also package your gear in a box, but it is not required. Luggage should
          be packed in a suitcase, duffel bag, or box. Bikes must be boxed.
        </>
      ),
      categoryId: 2,
    },
    {
      question: 'How should I pack my clubs or luggage?',
      answer: (
        <>
          Click{' '}
          <a className="font-bold text-blue-600 underline hover:text-blue-800" href="/pack">
            here
          </a>{' '}
          for more information on packing winter gear and luggage.
        </>
      ),
      categoryId: 2,
    },
    {
      question: 'Can I lock my domestic shipment?',
      answer: <>If you’re shipping domestically within the U.S., your bag may be locked.</>,
      categoryId: 2,
    },
    {
      question: 'Can I lock my international shipment?',
      answer: (
        <>
          If you’re shipping internationally, locks are NOT allowed due to customs inspections.
          Locked international shipments can cause delays and will void our shipment protection
          policy.
        </>
      ),
      categoryId: 2,
    },
    {
      question: 'Are there size or weight limits?',
      answer: (
        <>
          <p>
            Yes. Please identify your bag’s maximum dimensions (length + width + height) and weight
            to determine which bag size category it fits into. If your shipment exceeds the size or
            weight limits for that category, please{' '}
            <a className="font-bold text-blue-600 underline hover:text-blue-800" href="/contact">
              contact
            </a>{' '}
            our team - we’ll be happy to provide a custom quote for your shipment.
          </p>
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-6 sm:px-2 md:px-6">
            <table className="w-full table-auto border-collapse">
              <thead>
                <tr>
                  <th className="border-b border-gray-200 pb-1 text-left">Bag Type</th>
                  <th className="border-b border-gray-200 pb-1 text-left">Max Dimensions</th>
                  <th className="border-b border-gray-200 pb-1 text-left">Max Weight</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 py-1">Skis & Boards: Small</td>
                  <td className="border-b border-gray-200 py-1">94</td>
                  <td className="border-b border-gray-200 py-1">35</td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 py-1">Skis & Boards: Standard</td>
                  <td className="border-b border-gray-200 py-1">98</td>
                  <td className="border-b border-gray-200 py-1">50</td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 py-1">Skis & Boards: Large</td>
                  <td className="border-b border-gray-200 py-1">102</td>
                  <td className="border-b border-gray-200 py-1">65</td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 py-1">Luggage: Checked</td>
                  <td className="border-b border-gray-200 py-1">62</td>
                  <td className="border-b border-gray-200 py-1">50</td>
                </tr>
                <tr>
                  <td className="py-1">Luggage: Oversized</td>
                  <td className="py-1">74</td>
                  <td className="py-1">65</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      ),
      categoryId: 2,
    },
    {
      question: 'What happens if my bag is overweight?',
      answer: (
        <>
          If your bag weighs more than the limit for your chosen size, there could be extra fees and
          possible delays. To keep things running smoothly, we recommend sizing up if you’re unsure
          - better safe than sorry!
        </>
      ),
      categoryId: 2,
    },

    {
      question: 'What if I can’t weigh my bag?',
      answer: (
        <>
          Give your best estimate when booking, or head to a local FedEx, UPS, or DHL to have your
          bag weighed. If the weight is significantly heavier than expected, additional fees may
          apply.
        </>
      ),
      categoryId: 2,
    },
    {
      question: 'How do I attach the shipping labels?',
      answer: (
        <>
          <p>
            <strong>If you book at least 3 business days in advance,</strong> and your shipment
            originates in the U.S, we&apos;ll send labels (in tags) to your chosen address.
          </p>
          <p className="mt-4">
            <strong>If you don’t meet that criteria,</strong> and are using a hard travel case or
            box, attach your label with clear packing tape, making sure the barcode is visible and
            secure. If your bag doesn’t have a hard surface, you’ll need to place the label on a
            luggage baggage tag (also known as a fly tag), which you can pick up from your local
            FedEx, UPS, or DHL location, and attach it to the handle. Don’t forget to put a second
            copy of your shipping label in your bag, too.
          </p>
        </>
      ),
      categoryId: 2,
    },
    {
      question: 'What if I don’t have a printer?',
      answer: (
        <>
          <p>
            <strong>If you book at least 3 business days in advance,</strong> and your shipment
            originates in the U.S, we&apos;ll send labels (in tags) to your chosen address.
          </p>
          <p className="mt-4">
            <strong>If you don’t meet that criteria,</strong> you can email the shipping label to
            your local FedEx, UPS, or DHL for printing.
          </p>
        </>
      ),
      categoryId: 2,
    },
    {
      question: 'Does ShipSkis provide packaging?',
      answer: (
        <>
          We don’t supply packaging, but no worries - you can simply use your own hard case, soft
          travel bag, or box to ship your gear.
        </>
      ),
      categoryId: 2,
    },
    {
      question: 'Do I need a box?',
      answer: (
        <>
          Nope, no box needed! If you&apos;re shipping skis or snowboards in an airline-approved
          soft travel bag or hard case, you&apos;re good to go. Same goes for luggage - as long as
          it&apos;s packed in a suitcase or duffle bag.
        </>
      ),
      categoryId: 2,
    },
    {
      question: 'Where do I get a box?',
      answer: (
        <>
          If you have a soft travel bag or hard case, you don&apos;t need a box - that&apos;s the
          beauty of our service. However, if you need a box, you can usually buy one at your local
          UPS or FedEx store for a small fee (prices vary by location). We also sell ski boxes in
          packs of 5 for $49.99 - just reach out to Customer Support to order.
        </>
      ),
      categoryId: 2,
    },
    {
      question: 'Do I have to pack my bag differently if I am shipping internationally?',
      answer: (
        <>
          <p>
            For international shipments, <strong>no locks are allowed,</strong> including TSA locks,
            since customs may need to inspect your package.
          </p>
          <p className="mt-4">
            Here’s what you can include in your <strong>ski or snowboard bag:</strong>
            skis or snowboard, poles, boots, helmets, goggles, and gloves. For{' '}
            <strong>luggage,</strong> you can pack worn clothing, new clothing with price tags
            removed, footwear, headwear, and undergarments.
          </p>
          <p className="mt-4">
            Some things are <strong>off-limits,</strong> like toiletries (soap, shampoo,
            toothpaste), medications, pressurized containers (aerosol cans or CO2 cartridges), cash
            or negotiable items, electronics, batteries, alcohol or anything containing alcohol
            (including perfume and cologne), tobacco, firearms and ammunition, and food, vitamins,
            or other consumables.
          </p>
          <p className="mt-4">
            Before shipping, please remove all price tags. For the full list of prohibited items,
            check out our{' '}
            <a className="font-bold text-blue-600 underline hover:text-blue-800" href="/terms">
              Terms of Use
            </a>
            .
          </p>
        </>
      ),
      categoryId: 2,
    },
    {
      question: 'Can I add luggage to my order?',
      answer: (
        <>
          Yes! Adding pieces of luggage to your order is simple. Just scroll down to the traveler
          section, below the pickup and delivery dates, and you’ll see an option labeled Luggage
          Count. From the dropdown menu, select the number of pieces you want to ship and choose the
          type.
        </>
      ),
      categoryId: 2,
    },
    {
      question: 'Can I add hotel check-in information to my shipping label?',
      answer: (
        <>
          Yes, under &lsquo;Add additional details&rsquo; on step 3 of the booking process, you can
          type any information you would like into the &lsquo;Comments&rsquo; field. For example:
          Confirmation Number XXXXX, Checking in XX/XX/XX, Guest of John Smith, etc.
        </>
      ),
      categoryId: 2,
    },
    {
      question: 'Who will be picking up my shipment?',
      answer: (
        <>
          One of our trusted logistics partners, either FedEx, UPS, or DHL, will pick up your winter
          gear and luggage. Your shipping label will indicate which carrier will handle your pickup.
        </>
      ),
      categoryId: 3,
    },
    {
      question: 'Can I get my bags picked up from my home?',
      answer: (
        <>
          Yes! We can pick your gear up from your home, office, resort, or hotel - or you can simply
          drop it off at your nearest carrier store for added convenience.
        </>
      ),
      categoryId: 3,
    },
    {
      question: 'Is there a fee for dropping off my gear?',
      answer: (
        <>
          FedEx Offices and UPS Stores do not charge fees to drop off. However, some third-party
          pickup and drop-off locations may charge package handling fees. These fees are separate
          from our service and can vary depending on the location. To avoid surprises, we recommend
          calling ahead to check if any fees apply.
        </>
      ),
      categoryId: 3,
    },
    {
      question: 'Can I choose my carrier drop-off location?',
      answer: (
        <>
          While we assign your carrier (FedEx, UPS, or DHL) when you book, you’re free to drop off
          your shipment at any local location serviced by that carrier. Click{' '}
          <a
            className="font-bold text-blue-600 underline hover:text-blue-800"
            href="/drop-off-locator"
          >
            here
          </a>{' '}
          to find a drop-off location near you.
        </>
      ),
      categoryId: 3,
    },
    {
      question: 'Do I have to be home when my bags are picked up?',
      answer: (
        <>
          You don’t need to be home for pickup. Just leave your securely packed and labeled shipment
          outside your front door during the pickup window, at your discretion. If you don’t want to
          leave it unattended and can’t be home, you can drop it off at a local FedEx, UPS, or DHL
          store - just make sure it matches the carrier listed on your shipping label.
        </>
      ),
      categoryId: 3,
    },
    {
      question: 'Can I cancel my order?',
      answer: (
        <>
          Yes, you may cancel your order using the ‘Edit Shipment’ link in your confirmation email,
          tracking page, or by logging into your account and selecting the shipment you want to
          cancel.
        </>
      ),
      categoryId: 3,
    },
    {
      question: 'Can I make changes to my order?',
      answer: (
        <>
          Yes! You can update your order using the ‘Edit Shipment’ link in your confirmation email,
          tracking page, or by logging into your account and selecting the shipment you want to
          modify. Just make sure to do it before your shipment is in transit. Once it’s on the move,
          you’ll need to contact customer support to request changes, and fees may apply.
        </>
      ),
      categoryId: 3,
    },
    {
      question: 'How long does delivery take?',
      answer: (
        <>
          Delivery times vary depending on the service level you choose. Ground shipments typically
          arrive within 2 to 6 business days, depending on the route, while expedited options can
          get your gear there even faster - some even as quickly as the next business day. After
          pickup, you’ll receive tracking updates so you can monitor your shipment every step of the
          way.
        </>
      ),
      categoryId: 3,
    },
    {
      question: 'Is a signature required upon delivery?',
      answer: (
        <>
          A signature is not required, but it’s ultimately up to the driver’s discretion. If the
          driver considers the delivery area secure or your destination has a shipping/receiving
          area, your shipment will be delivered without a signature. If this is a concern, we
          recommend shipping to a supervised location, like a business, golf course, resort, friend
          or family member, or carrier store (fees may apply).
        </>
      ),
      categoryId: 3,
    },
    {
      question: 'Does ShipSkis deliver on weekends?',
      answer: (
        <>
          Deliveries can only be scheduled Monday through Friday to ensure your shipment is handled
          during regular business days when all of our partnered carriers are operating. That said,
          depending on the route, your gear might still arrive on a weekend.
        </>
      ),
      categoryId: 3,
    },
    {
      question:
        'Do I need to confirm that the ski resort, ' +
        'rental home, or hotel will accept my shipment?',
      answer: (
        <>
          Before placing your order, we do recommend reaching out to your destination to let them
          know about your shipment and your expected arrival date.
        </>
      ),
      categoryId: 3,
    },
    {
      question: 'Do hotels and resorts charge to receive/hold my gear?',
      answer: (
        <>
          Policies vary by location. Some properties may charge a fee for receiving or holding
          shipments, so it’s best to check directly with them beforehand.
        </>
      ),
      categoryId: 3,
    },
    {
      question:
        'Can I have my bag returned to a different address than the one ' +
        'it was originally shipped from?',
      answer: (
        <>
          That’s easy! Just book two separate one-way shipments - one to send your bag out, and
          another to have it returned to a different address. Don’t worry, the cost is the same as a
          round-trip shipment. This also works if your trip has multiple stops and you need your bag
          sent to different places along the way.
        </>
      ),
      categoryId: 3,
    },
    {
      question: 'How do I track my shipment?',
      answer: (
        <>
          <p>
            We&apos;ll keep you posted with email updates and optional text alerts when your
            shipment is picked up, out for delivery, and delivered. You&apos;ll also find a tracking
            link in your confirmation email. To track through your account, simply:
          </p>
          <p className="mt-4">Visit www.ShipSkis.com</p>
          <p className="mt-4">Log in (top-right corner)</p>
          <p className="mt-4">Go to My Account {'>'} Shipments</p>
          <p className="mt-4">Select the shipment you&apos;d like to track</p>
        </>
      ),
      categoryId: 4,
    },
    {
      question: 'Will I get notifications for delivery updates?',
      answer: (
        <>
          Yes! You’ll receive real-time tracking notifications via email so you always know where
          your shipment is and when to expect delivery. You can opt-in to receive notifications via
          text as well.
        </>
      ),
      categoryId: 4,
    },
    {
      question: 'What if my shipment is delayed or lost?',
      answer: (
        <>
          Although delays and losses are rare, if we suspect either might have occurred, our
          customer support team will reach out to you. We’ll promptly investigate and work to
          resolve the issue as quickly as possible.
        </>
      ),
      categoryId: 4,
    },
    {
      question: 'Do you ship internationally?',
      answer: (
        <>We service 180 countries and territories, and handle all the tricky customs paperwork.</>
      ),
      categoryId: 4,
    },
    {
      question: 'How much does it cost to ship skis or a snowboard?',
      answer: (
        <>
          The cost to ship your gear varies depending on factors like your shipping distance and the
          service level you choose. To get an exact quote, simply enter your pickup and delivery
          locations on our{' '}
          <a className="font-bold text-blue-600 underline hover:text-blue-800" href="/">
            website
          </a>{' '}
          or{' '}
          <a className="font-bold text-blue-600 underline hover:text-blue-800" href="/contact">
            contact
          </a>{' '}
          our customer support team for personalized assistance.
        </>
      ),
      categoryId: 5,
    },
    {
      question: 'Is insurance included? Can I add more coverage?',
      answer: (
        <>
          Domestic shipments come with $500 of insurance for skis, snowboards, boots, luggage, and
          boxes. $1000 is included for bikes. Need more coverage? You can add extra for a small fee.
          We recommend choosing an amount that reflects the full value of your gear and the bag it’s
          packed in.
        </>
      ),
      categoryId: 5,
    },
    {
      question: 'What is covered under insurance?',
      answer: (
        <>
          ShipSkis’ shipment insurance protects against complete loss of your shipped item or
          against any physical damage to qualified items that has occurred while in transit. For
          items not shipped in a box, the outside casing is considered the shipping container.
          Please note that general wear and tear, along with damage to protruding parts such as
          wheels, straps, pockets, handles, or other attachments on a travel case or bag, are not
          covered under this protection.
        </>
      ),
      categoryId: 5,
    },
    {
      question: 'How do I file a damage/missing item claim?',
      answer: (
        <>
          In order to file a damage or missing items claim, a{' '}
          <a className="font-bold text-blue-600 underline hover:text-blue-800" href="/claims">
            claims form
          </a>{' '}
          must be submitted to a claims representative. Claims requirements are outlined on our
          claims form.
        </>
      ),
      categoryId: 5,
    },
    {
      question: 'How long does it take to resolve a claim?',
      answer: (
        <>
          <p>
            A claims specialist will review and file your claim within 1-2 business days. If
            additional information is needed, a claims specialist will contact you by phone or
            email. Otherwise, you will be made aware of your claim being filed.
          </p>
          <p className="mt-4">
            Once your claim has been reviewed and filed, you can expect to receive an approval or
            denial within 14 business days. Once the claim has been filed, it cannot be altered.
          </p>
        </>
      ),
      categoryId: 5,
    },
    {
      question: 'Can I ship luggage or oversized items',
      answer: (
        <>
          Yes! You can ship luggage along with your skis and snowboard. For specialty items like
          oversized gear, just{' '}
          <a className="font-bold text-blue-600 underline hover:text-blue-800" href="/contact">
            contact
          </a>{' '}
          our team - we’re happy to help arrange those shipments.
        </>
      ),
      categoryId: 6,
    },
    {
      question: 'How do I get a custom quote for special shipments?',
      answer: (
        <>
          To get a custom quote for specialty or oversized shipments, please{' '}
          <a className="font-bold text-blue-600 underline hover:text-blue-800" href="/contact">
            contact
          </a>{' '}
          our customer support team with your shipment details. We&apos;ll work with you to provide
          a personalized price and shipping plan.
        </>
      ),
      categoryId: 6,
    },
    {
      question: 'Are there items you cannot ship?',
      answer: (
        <>
          Yes, there are certain items we cannot ship for safety and legal reasons. These include
          hazardous materials, perishable goods, and restricted items as outlined in our shipping
          policy. If you&apos;re unsure whether your item qualifies, please{' '}
          <a className="font-bold text-blue-600 underline hover:text-blue-800" href="/contact">
            contact
          </a>{' '}
          us before booking.
        </>
      ),
      categoryId: 6,
    },
    {
      question: 'How do I contact customer support?',
      answer: (
        <>
          Click{' '}
          <a className="font-bold text-blue-600 underline hover:text-blue-800" href="/contact">
            here
          </a>{' '}
          to contact us.
        </>
      ),
      categoryId: 7,
    },
  ],
  button: {
    moreText: 'View More FAQs',
  },
  variant: 'blue',
}

const content: HowItWorksPageContent = {
  faq,
  hero,
  steps,
  fifties,
  breaker,
  feature,
  videos,
  informationCards,
}

export default content
